import { HttpService } from '@src/api/common/HttpService';
import { UrlService } from '@src/api/common/UrlService';

type EmployeeType = {
  id: number;
};

// Dummy data to simulate API responses
const dummyEmployees = [
  {
    id: 1,
    name: 'Alice Johnson',
    age: 28,
    email: 'alice.johnson@company.com',
    department: 'Engineering',
    position: 'Senior Developer',
    salary: 75000,
    createdDate: '2023-01-15',
    createdBy: 'HR System',
    updatedDate: '2024-08-15',
    updatedBy: 'Manager',
  },
  {
    id: 2,
    name: 'Bob Smith',
    age: 34,
    email: 'bob.smith@company.com',
    department: 'Marketing',
    position: 'Marketing Manager',
    salary: 68000,
    createdDate: '2023-03-20',
    createdBy: 'HR System',
    updatedDate: '2024-08-10',
    updatedBy: 'HR Admin',
  },
  {
    id: 3,
    name: 'Charlie Brown',
    age: 25,
    email: 'charlie.brown@company.com',
    department: 'Design',
    position: 'UI/UX Designer',
    salary: 62000,
    createdDate: '2023-06-10',
    createdBy: 'HR System',
    updatedDate: '2024-08-20',
    updatedBy: 'Team Lead',
  },
  {
    id: 4,
    name: 'Diana Prince',
    age: 31,
    email: 'diana.prince@company.com',
    department: 'HR',
    position: 'HR Specialist',
    salary: 58000,
    createdDate: '2023-02-28',
    createdBy: 'HR System',
    updatedDate: '2024-08-18',
    updatedBy: 'HR Director',
  },
  {
    id: 5,
    name: 'Edward Wilson',
    age: 42,
    email: 'edward.wilson@company.com',
    department: 'Finance',
    position: 'Financial Analyst',
    salary: 72000,
    createdDate: '2023-04-05',
    createdBy: 'HR System',
    updatedDate: '2024-08-12',
    updatedBy: 'Finance Manager',
  },
];

export const EmployeeService = () => {
  // Using the existing HttpService pattern but simulating responses for demo
  const { HttpGet, HttpDelete, HttpPost } = HttpService();

  const GetEmployeeList = async (query: string) => {
    // Simulate API delay
    return new Promise((resolve) => {
      setTimeout(() => {
        // Parse query parameters for filtering
        let filteredData = [...dummyEmployees];

        // Parse the query string to extract filters
        const urlParams = new URLSearchParams(
          query.startsWith('?') ? query.slice(1) : query
        );
        const nameFilter = urlParams.get('name') || '';
        const departmentFilter = urlParams.get('department') || '';

        // Apply name filter
        if (nameFilter) {
          filteredData = filteredData.filter((employee) =>
            employee.name.toLowerCase().includes(nameFilter.toLowerCase())
          );
        }

        // Apply department filter
        if (departmentFilter) {
          filteredData = filteredData.filter(
            (employee) => employee.department === departmentFilter
          );
        }

        resolve({
          status: 'success',
          data: {
            results: filteredData,
          },
        });
      }, 1000);
    });

    // In a real application, this would be:
    // const url = `${BaseApiUrl}/api/v1/employees${query}`;
    // const response = await HttpGet(url);
    // return response;
  };

  const GetEmployeeById = async (body: EmployeeType) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const employee = dummyEmployees.find((emp) => emp.id === body.id);
        resolve({
          status: 'success',
          data: employee || null,
        });
      }, 500);
    });

    // In a real application:
    // const suffix = UrlService.BuildQuery(body);
    // const url = `${BaseApiUrl}/api/v1/employees/detail${suffix}`;
    // const response = await HttpGet(url);
    // return response;
  };

  const CreateOrUpdateEmployeeById = async (body: any) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        let employee;
        if (body.id && dummyEmployees.some((emp) => emp.id === body.id)) {
          // Update existing employee
          const idx = dummyEmployees.findIndex((emp) => emp.id === body.id);
          dummyEmployees[idx] = { ...dummyEmployees[idx], ...body };
          employee = dummyEmployees[idx];
        } else {
          // Create new employee
          const newId = dummyEmployees.length > 0 ? Math.max(...dummyEmployees.map(e => e.id)) + 1 : 1;
          employee = { ...body, id: newId };
          dummyEmployees.push(employee);
        }
        resolve({
          status: 'success',
          data: employee,
        });
      }, 800);
    });

    // In a real application:
    // const url = `${BaseApiUrl}/api/v1/employees/detail`;
    // const response = await HttpPost(url, body);
    // return response;
  };

  const LogicDeleteEmployeeById = async (body: EmployeeType) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          status: 'success',
          message: 'Employee deleted successfully',
        });
      }, 500);
    });

    // In a real application:
    // const suffix = UrlService.BuildQuery(body);
    // const url = `${BaseApiUrl}/api/v1/employees/detail/delete${suffix}`;
    // const response = await HttpDelete(url);
    // return response;
  };

  return {
    GetEmployeeList,
    GetEmployeeById,
    CreateOrUpdateEmployeeById,
    LogicDeleteEmployeeById,
  };
};
